'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: string;
  badge?: number;
  group?: string;
}

const navItems: NavItem[] = [
  { id: 'nav-home', label: 'Home', href: '/', icon: 'HomeIcon', group: 'main' },
  { id: 'nav-bvo', label: 'Business Value Objects', href: '/business-value-objects', icon: 'CubeIcon', badge: 3, group: 'platform' },
  { id: 'nav-bvf', label: 'Value Flow Studio', href: '/business-value-flow-studio', icon: 'ArrowsRightLeftIcon', group: 'platform' },
  { id: 'nav-twin', label: 'Digital Twins', href: '/digital-twin-workspace', icon: 'CpuChipIcon', group: 'platform' },
  { id: 'nav-mining', label: 'Process Mining', href: '/process-mining', icon: 'MagnifyingGlassCircleIcon', group: 'analysis' },
  { id: 'nav-analytics', label: 'Analytics Center', href: '/analytics-center', icon: 'ChartBarSquareIcon', group: 'analysis' },
];

const groupLabels: Record<string, string> = {
  main: 'Overview',
  platform: 'Platform',
  analysis: 'Analysis',
};

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const pathname = usePathname();

  const groups = ['main', 'platform', 'analysis'];

  return (
    <aside
      className={`sidebar-transition flex flex-col bg-surface-2 border-r border-border h-screen sticky top-0 z-40 ${
        collapsed ? 'w-16 min-w-[64px]' : 'w-60 min-w-[240px]'
      }`}
    >
      {/* Logo */}
      <div className={`flex items-center border-b border-border h-16 px-3 ${collapsed ? 'justify-center' : 'gap-3'}`}>
        <AppLogo size={32} />
        {!collapsed && (
          <div className="flex flex-col min-w-0">
            <span className="font-semibold text-sm text-foreground leading-tight truncate">ProcessMiningX</span>
            <span className="text-2xs text-muted-foreground leading-tight">Business Value Platform</span>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-4">
        {groups.map((group) => {
          const items = navItems.filter((n) => n.group === group);
          if (!items.length) return null;
          return (
            <div key={`group-${group}`}>
              {!collapsed && (
                <p className="text-2xs font-semibold uppercase tracking-widest text-muted-foreground px-2 mb-1.5">
                  {groupLabels[group]}
                </p>
              )}
              <ul className="space-y-0.5">
                {items.map((item) => {
                  const isActive = item.href === '/' ? pathname === '/' : pathname.startsWith(item.href);
                  return (
                    <li key={item.id}>
                      <Link
                        href={item.href}
                        title={collapsed ? item.label : undefined}
                        className={`group flex items-center gap-3 rounded-lg px-2 py-2 text-sm font-medium transition-all duration-150 relative ${
                          isActive
                            ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                        } ${collapsed ? 'justify-center' : ''}`}
                      >
                        <Icon
                          name={item.icon as Parameters<typeof Icon>[0]['name']}
                          size={18}
                          className={`shrink-0 transition-colors ${isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'}`}
                        />
                        {!collapsed && (
                          <span className="truncate flex-1">{item.label}</span>
                        )}
                        {!collapsed && item.badge && item.badge > 0 && (
                          <span className="ml-auto bg-negative/20 text-negative text-2xs font-semibold px-1.5 py-0.5 rounded-full tabular-nums">
                            {item.badge}
                          </span>
                        )}
                        {collapsed && item.badge && item.badge > 0 && (
                          <span className="absolute top-1 right-1 w-2 h-2 bg-negative rounded-full" />
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>

      {/* Bottom */}
      <div className="border-t border-border p-2 space-y-1">
        <button
          onClick={onToggle}
          className={`flex items-center gap-3 rounded-lg px-2 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground w-full transition-all duration-150 ${collapsed ? 'justify-center' : ''}`}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Icon
            name={collapsed ? 'ChevronDoubleRightIcon' : 'ChevronDoubleLeftIcon'}
            size={16}
            className="shrink-0"
          />
          {!collapsed && <span className="text-xs">Collapse</span>}
        </button>
      </div>
    </aside>
  );
}